import React, { useContext, useEffect, useState } from 'react';
import c from 'classnames';
import { CommentEditorContext } from '../../../CommentEditor/context';
import EditorDropzone from '@wix/da-editor-tiptap/pkg/components/lego/EditorDropzone';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';
import ExclusiveSpiderWeb from './ExclusiveSpiderWeb';
import ExclusiveSpider from './ExclusiveSpider';
import { ExclusiveAvatarWithPopover } from '../../../Comment/lego/ExclusiveAvatar';
import { useSelector } from 'react-redux';
import { getCompliantLocalStorageItem } from '@wix/da-react-context/pkg/compliantLocalStorage/redux/selectors';
import { CompliantLocalStorageKeys } from '@wix/da-react-context/pkg/compliantLocalStorage/redux/constants';
import { PapiDeviation } from '@wix/da-papi-types';

import s from './CommentBoxContentWrapper.scss';

export interface Props {
  className?: string;
}

/**
 * Standard styling for the content of our comment box.
 * Also adds the global editor class we need for themeing inside of DaEditor
 */
const CommentBoxContentWrapper: React.FC<React.PropsWithChildren<Props>> = ({
  children,
  className,
}) => {
  const { isExpanded } = useContext(CommentEditorContext);
  const exclusive_avatars = useFlag('exclusive_avatars');
  const [preExpandExclusiveAvatar, setPreExpandExclusiveAvatar] = useState<
    PapiDeviation | undefined
  >(undefined);
  const lastExclusiveRaw = useSelector(state =>
    getCompliantLocalStorageItem(
      state as any,
      CompliantLocalStorageKeys.EXCLUSIVE_AVATAR_LAST_SELECTED
    )
  );

  // Show the saved exclusive avatar even before the editor expands/initializes
  useEffect(() => {
    if (!exclusive_avatars) return;
    if (isExpanded) return;

    if (!lastExclusiveRaw) {
      setPreExpandExclusiveAvatar(undefined);
      return;
    }
    const parsed = JSON.parse(lastExclusiveRaw) as PapiDeviation | null;
    if (parsed && (parsed as any).deviationId) {
      setPreExpandExclusiveAvatar(parsed);
    } else {
      setPreExpandExclusiveAvatar(undefined);
    }
  }, [exclusive_avatars, isExpanded, lastExclusiveRaw]);
  return (
    <div
      className={c(
        s['commentbox-content'],
        'da-editor-comment',
        isExpanded && s['expanded'],
        className
      )}
    >
      {exclusive_avatars && !isExpanded && preExpandExclusiveAvatar && (
        <ExclusiveAvatarWithPopover
          deviation={preExpandExclusiveAvatar}
          className={s['exclusive-avatar']}
        />
      )}
      <EditorDropzone>{children}</EditorDropzone>
      {exclusive_avatars && (
        <>
          <div className={s['exclusive-avatar-frame']} />
          <div className={s['exclusive-spider-wrapper']}>
            <div className={s['exclusive-spider-thread']} />
            <ExclusiveSpiderWeb className={s['exclusive-spider-web']} />
            <ExclusiveSpider className={s['exclusive-spider']} />
          </div>
        </>
      )}
    </div>
  );
};
CommentBoxContentWrapper.displayName = 'CommentBoxContentWrapper';

export default CommentBoxContentWrapper;
